import {
  Box,
  Button,
  HStack,
  IconButton,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t } from "@lingui/macro";
import { SortOrder } from "@src/__generated__/graphql";
import { useTableHeaderGroup } from "@src/components/ui-kit/DataTable/Body/hooks/header-group";
import type { DataTableBodyStore } from "@src/components/ui-kit/DataTable/Body/store";
import { DragHandle } from "@src/components/ui-kit/DragHandle";
import { Icon } from "@src/components/ui-kit/Icon";
import type { Header, Table } from "@tanstack/react-table";
import type { CSSProperties, ReactNode } from "react";
import type { BodyColumnMeta, BodyProps, BodyRow } from "../../types";
import {
  getColumnMeta,
  getCommonPinningStyles,
  getHeaderSizeCssVar,
} from "../helpers";

type TableHeaderCellProps<Row extends BodyRow> = {
  header: Header<Row, unknown>;
  headerContent: ReactNode;
  isReorderable: boolean | undefined;
  showResizeHandle: boolean | undefined;
};
const TableHeaderCell = <Row extends BodyRow>({
  header,
  headerContent,
  isReorderable,
  showResizeHandle,
}: TableHeaderCellProps<Row>) => {
  const headerSizeCssVar = getHeaderSizeCssVar(header.id);
  const align = getColumnMeta(header.column.columnDef)?.align;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: header.column.id,
    disabled: !isReorderable,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(
      isDragging && transform
        ? { ...transform, scaleX: 1, scaleY: 1 }
        : transform,
    ),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  const pinningStyles = getCommonPinningStyles(header.column);

  return (
    <Th
      ref={setNodeRef}
      pos="relative"
      verticalAlign="middle"
      w={`calc(var(${headerSizeCssVar}) * 1px)`}
      minW={`calc(var(${headerSizeCssVar}) * 1px)`}
      maxW={`calc(var(${headerSizeCssVar}) * 1px)`}
      py="2.5"
      pr="1.5"
      pl="3"
      bg="gray.50"
      opacity={isDragging ? 0.6 : 1}
      whiteSpace="nowrap"
      role="group"
      style={{ ...style, ...pinningStyles }}
    >
      {header.isPlaceholder ? null : (
        <HStack justify={align} spacing="2">
          <Box overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
            {headerContent}
          </Box>

          {isReorderable && (
            <IconButton
              pos="absolute"
              right={align !== "right" ? 2 : undefined}
              left={align === "right" ? 2 : undefined}
              w="0"
              opacity={isDragging ? 1 : 0}
              _groupHover={{
                opacity: 1,
                pointerEvents: "auto",
                w: 6,
              }}
              pointerEvents={isDragging ? "auto" : "none"}
              transition="opacity 0.15s ease"
              aria-label={t`Move column`}
              colorScheme="gray"
              icon={<DragHandle />}
              size="xs"
              variant="ghost"
              {...attributes}
              {...listeners}
            />
          )}
        </HStack>
      )}

      {showResizeHandle && (
        <Box
          pos="absolute"
          top="0"
          right="0"
          w="1"
          h="full"
          bg={header.column.getIsResizing() ? "purple.500" : "transparent"}
          opacity={header.column.getIsResizing() ? 1 : 0}
          _groupHover={{
            bg: "gray.200",
            opacity: 1,
            pointerEvents: "auto",
          }}
          userSelect="none"
          pointerEvents={header.column.getIsResizing() ? "auto" : "none"}
          cursor="col-resize"
          transition="opacity 0.15s ease"
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          style={{ touchAction: "none" }}
        />
      )}
    </Th>
  );
};

type TableSortableHeaderProps = {
  label: ReactNode;
  order: SortOrder | undefined;
  onClick: () => void;
  align?: BodyColumnMeta["align"];
};
const TableSortableHeader = ({
  label,
  order,
  onClick,
  align,
}: TableSortableHeaderProps) => {
  const iconName =
    order === SortOrder.Desc
      ? "chevron-down"
      : order === SortOrder.Asc
        ? "chevron-up"
        : "chevron-selector-vertical";

  return (
    <Button
      color="inherit"
      fontSize="inherit"
      fontWeight="inherit"
      textAlign={align}
      fontStyle="inherit"
      textTransform="inherit"
      onClick={onClick}
      rightIcon={<Icon name={iconName} />}
      size="sm"
      variant="link"
    >
      {label}
    </Button>
  );
};

interface TableHeaderCellRendererProps<Row extends BodyRow, OrderByColumn>
  extends Pick<
    TableHeaderProps<Row, OrderByColumn>,
    | "enableColumnOrdering"
    | "enableColumnResizing"
    | "shouldShowColumnVisibilityControl"
    | "columnVisibilityControlWithAddon"
    | "dataColumnIdsSet"
    | "orderedBy"
    | "onOrderByChange"
  > {
  header: Header<Row, unknown>;
}
const TableHeaderCellRenderer = <Row extends BodyRow, OrderByColumn>({
  header,
  columnVisibilityControlWithAddon,
  dataColumnIdsSet,
  enableColumnOrdering,
  enableColumnResizing,
  shouldShowColumnVisibilityControl,
  onOrderByChange,
  orderedBy,
}: TableHeaderCellRendererProps<Row, OrderByColumn>) => {
  const {
    isReorderable,
    activeOrder,
    headerContent,
    isSortable,
    handleOrderByChange,
    showResizeHandle,
  } = useTableHeaderGroup<Row, OrderByColumn>({
    header,
    columnVisibilityControlWithAddon,
    dataColumnIdsSet,
    enableColumnOrdering,
    enableColumnResizing,
    shouldShowColumnVisibilityControl,
    onOrderByChange,
    orderedBy,
  });

  const sortableHeaderContent = isSortable ? (
    <TableSortableHeader
      label={headerContent}
      order={activeOrder}
      onClick={handleOrderByChange}
    />
  ) : (
    headerContent
  );

  return (
    <TableHeaderCell
      key={header.id}
      header={header}
      headerContent={sortableHeaderContent}
      isReorderable={isReorderable}
      showResizeHandle={showResizeHandle}
    />
  );
};

export interface TableHeaderProps<Row extends BodyRow, OrderByColumn>
  extends Pick<
    BodyProps<Row, OrderByColumn>,
    | "orderedBy"
    | "onOrderByChange"
    | "enableColumnOrdering"
    | "enableColumnResizing"
  > {
  table: Table<Row>;
  dataColumnOrder: string[];
  dataColumnIds: string[];
  dataColumnIdsSet: Set<string>;
  shouldShowColumnVisibilityControl: boolean;
  columnVisibilityControlWithAddon: ReactNode;
  onDragEnd: DataTableBodyStore["handleDragEnd"];
}
export const TableHeader = <Row extends BodyRow, OrderByColumn>({
  table,
  dataColumnOrder,
  dataColumnIds,
  dataColumnIdsSet,
  enableColumnOrdering,
  enableColumnResizing,
  shouldShowColumnVisibilityControl,
  columnVisibilityControlWithAddon,
  orderedBy,
  onOrderByChange,
  onDragEnd,
}: TableHeaderProps<Row, OrderByColumn>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    onDragEnd(
      event,
      enableColumnOrdering ?? false,
      dataColumnIds,
      dataColumnIdsSet,
    );
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      sensors={sensors}
      modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
    >
      <Thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <SortableContext
            key={headerGroup.id}
            items={dataColumnOrder}
            strategy={horizontalListSortingStrategy}
          >
            <Tr bg="gray.50">
              {headerGroup.headers.map((header) => (
                <TableHeaderCellRenderer
                  key={header.id}
                  header={header}
                  enableColumnOrdering={enableColumnOrdering}
                  enableColumnResizing={enableColumnResizing}
                  shouldShowColumnVisibilityControl={
                    shouldShowColumnVisibilityControl
                  }
                  columnVisibilityControlWithAddon={
                    columnVisibilityControlWithAddon
                  }
                  dataColumnIdsSet={dataColumnIdsSet}
                  orderedBy={orderedBy}
                  onOrderByChange={onOrderByChange}
                />
              ))}
            </Tr>
          </SortableContext>
        ))}
      </Thead>
    </DndContext>
  );
};
